// Live order feed for the ticker bar.
//
// Weighted, persisted, and DISPATCH-HOURS AWARE.
//
//   * WEIGHTED, not uniform-random — the mix leans toward what actually sells
//     (5/8" Minus and topsoil dominate; Seattle/Tacoma/Kent dominate), so it
//     reads like a real dispatch board rather than a shuffle.
//   * BUSINESS HOURS ONLY — orders are only ever placed inside dispatch hours
//     (Mon–Fri 5a–6p, Sat 6a–3p, Sun closed, Pacific). Early on a Monday the
//     board is therefore mostly Saturday's runs, and it refills with today's
//     as the day goes on.
//   * PERSISTED with absolute timestamps — the feed is stored, so an order
//     that says "1 hr ago" says "2 hr ago" an hour later, then rolls over to
//     "Yesterday 4:15 PM" and eventually falls off the board.

export type LiveFeedOrder = {
  material: string
  quantity: number
  city: string
  /** Epoch ms. Absolute so relative time keeps counting up across reloads. */
  createdAt: number
}

const STORAGE_KEY = '206gravel_live_feed_v2'
const FEED_SIZE = 6
/** Anything older than this is stale storage, not a plausible "recent" order. */
const MAX_AGE_MS = 4 * 24 * 60 * 60 * 1000
/** Plausible spacing between consecutive orders, in dispatch minutes. */
const MIN_GAP_MS = 18 * 60 * 1000
const MAX_GAP_MS = 74 * 60 * 1000

const MINUTE = 60 * 1000

// ── Dispatch hours (Pacific) ────────────────────────────────────────────────
// Matches the hours the hero trust strip advertises.
function hoursFor(day: number): { open: number; close: number } | null {
  if (day === 0) return null // Sunday — closed
  if (day === 6) return { open: 6 * 60, close: 15 * 60 } // Saturday 6a–3p
  return { open: 5 * 60, close: 18 * 60 } // Mon–Fri 5a–6p
}

/** Day-of-week + minutes-since-midnight for a timestamp, in Pacific time. */
function pacificParts(ts: number): { day: number; minutes: number } {
  const pt = new Date(
    new Date(ts).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
  )
  return { day: pt.getDay(), minutes: pt.getHours() * 60 + pt.getMinutes() }
}

function isDispatchOpen(ts: number): boolean {
  const { day, minutes } = pacificParts(ts)
  const hours = hoursFor(day)
  if (!hours) return false
  return minutes >= hours.open && minutes < hours.close
}

/**
 * Walk a timestamp BACKWARD to the most recent moment dispatch was open.
 * A Sunday afternoon rolls back to Saturday 3pm; 4am Monday rolls back to
 * Saturday 3pm as well.
 */
function rollBackToOpen(ts: number): number {
  let cursor = ts
  for (let i = 0; i < 12; i++) {
    const { day, minutes } = pacificParts(cursor)
    const hours = hoursFor(day)
    if (hours && minutes >= hours.open && minutes < hours.close) return cursor
    if (hours && minutes >= hours.close) {
      // After close today — step back to closing time.
      cursor -= (minutes - hours.close + 1) * MINUTE
    } else {
      // Before open, or a closed day — step back past midnight.
      cursor -= (minutes + 1) * MINUTE
    }
  }
  return cursor
}

/** Walk a timestamp FORWARD to the next moment dispatch is open. */
function rollForwardToOpen(ts: number): number {
  let cursor = ts
  for (let i = 0; i < 12; i++) {
    const { day, minutes } = pacificParts(cursor)
    const hours = hoursFor(day)
    if (hours && minutes >= hours.open && minutes < hours.close) return cursor
    if (hours && minutes < hours.open) {
      cursor += (hours.open - minutes) * MINUTE
    } else {
      // After close, or a closed day — jump to the start of the next day.
      cursor += (24 * 60 - minutes) * MINUTE
    }
  }
  return cursor
}

// ── Weighted content ────────────────────────────────────────────────────────
type Weighted<T> = { value: T; weight: number }

// Mix mirrors real order volume: base rock and topsoil carry the board, decor
// and specialty products appear occasionally.
const MATERIALS: Weighted<{ name: string; yards: number[] }>[] = [
  { value: { name: '5/8" Minus', yards: [12, 12, 12, 24, 8, 6] }, weight: 26 },
  { value: { name: '3-Way Topsoil', yards: [15, 15, 10, 30, 5] }, weight: 18 },
  { value: { name: '7/8" Drain Rock', yards: [12, 6, 4, 10] }, weight: 12 },
  { value: { name: 'Medium Bark', yards: [15, 15, 8, 30] }, weight: 11 },
  { value: { name: '1.25" Minus', yards: [12, 12, 24, 10] }, weight: 9 },
  { value: { name: 'Pea Gravel', yards: [6, 4, 12, 8] }, weight: 7 },
  { value: { name: 'Recycled Concrete', yards: [12, 24, 12] }, weight: 6 },
  { value: { name: '5/8" Crushed Clear', yards: [12, 8, 6] }, weight: 5 },
  { value: { name: 'Walkway Gravel', yards: [4, 6, 8] }, weight: 3 },
  { value: { name: '50/50 Mix', yards: [10, 15, 5] }, weight: 3 },
]

// Delivery volume by city — Seattle and the south-end corridor carry most of
// the runs, the Eastside and outlying cities appear less often.
const CITIES: Weighted<string>[] = [
  { value: 'Seattle', weight: 20 },
  { value: 'Tacoma', weight: 16 },
  { value: 'Kent', weight: 11 },
  { value: 'Renton', weight: 10 },
  { value: 'Auburn', weight: 8 },
  { value: 'Puyallup', weight: 8 },
  { value: 'Federal Way', weight: 7 },
  { value: 'Bellevue', weight: 6 },
  { value: 'Burien', weight: 5 },
  { value: 'Shoreline', weight: 4 },
  { value: 'Redmond', weight: 3 },
  { value: 'Lynnwood', weight: 2 },
]

function pickWeighted<T>(options: Weighted<T>[]): T {
  const total = options.reduce((sum, o) => sum + o.weight, 0)
  let roll = Math.random() * total
  for (const option of options) {
    roll -= option.weight
    if (roll <= 0) return option.value
  }
  return options[options.length - 1].value
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]
}

function randomGap(): number {
  return MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS)
}

function makeOrder(createdAt: number, avoidCity?: string): LiveFeedOrder {
  const material = pickWeighted(MATERIALS)
  let city = pickWeighted(CITIES)
  // Two identical cities back to back reads as a bug, so re-roll once.
  if (avoidCity && city === avoidCity) city = pickWeighted(CITIES)
  return {
    material: material.name,
    quantity: pick(material.yards),
    city,
    createdAt: Math.round(createdAt),
  }
}

// ── Storage ─────────────────────────────────────────────────────────────────
function isValid(entry: unknown): entry is LiveFeedOrder {
  if (!entry || typeof entry !== 'object') return false
  const o = entry as LiveFeedOrder
  return (
    typeof o.material === 'string' &&
    typeof o.city === 'string' &&
    typeof o.quantity === 'number' &&
    typeof o.createdAt === 'number' &&
    Number.isFinite(o.createdAt)
  )
}

function read(): LiveFeedOrder[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isValid) : []
  } catch {
    return []
  }
}

function write(orders: LiveFeedOrder[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(orders))
  } catch {
    /* private window / quota — the in-memory feed still works */
  }
}

/**
 * Returns the current board, newest first.
 *
 * Every timestamp lands inside dispatch hours, so the board reads correctly at
 * any time of day: at 8am Monday it shows a couple of this-morning runs on top
 * of Saturday's, and by afternoon it has filled with today's.
 */
export function getLiveFeed(now: number = Date.now()): LiveFeedOrder[] {
  const orders = read()
    .filter((o) => o.createdAt <= now && now - o.createdAt < MAX_AGE_MS)
    .sort((a, b) => b.createdAt - a.createdAt)

  if (orders.length === 0) {
    // Cold start: walk backwards through dispatch hours from the last open
    // moment, skipping over nights, Sundays, and pre-open hours.
    let cursor = rollBackToOpen(now) - Math.random() * 12 * MINUTE
    for (let i = 0; i < FEED_SIZE; i++) {
      cursor = rollBackToOpen(cursor)
      orders.push(makeOrder(cursor, orders[i - 1]?.city))
      cursor -= randomGap()
    }
  } else {
    // Warm start: let any orders that "arrived" since the last visit land on
    // top — but only at moments dispatch was actually open.
    let cursor = rollForwardToOpen(orders[0].createdAt + randomGap())
    let guard = 0
    while (cursor <= now && guard++ < 40) {
      orders.unshift(makeOrder(cursor, orders[0]?.city))
      cursor = rollForwardToOpen(cursor + randomGap())
    }
    // Backfill if storage held fewer than a full board.
    let oldest = orders[orders.length - 1].createdAt
    while (orders.length < FEED_SIZE) {
      oldest = rollBackToOpen(oldest - randomGap())
      orders.push(makeOrder(oldest, orders[orders.length - 1]?.city))
    }
  }

  const trimmed = orders.slice(0, FEED_SIZE)
  write(trimmed)
  return trimmed
}

/** True when the given moment falls inside dispatch hours. */
export { isDispatchOpen }
